import Head from "next/head";
import { useRouter } from "next/router";
import React from "react";
import {
  BreadcrumbContainer,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbNav,
  BreadcrumbSeparator,
} from "./Breadcrumb.style";

interface BreadcrumbProps {
  className?: string;
  hostname?: string;
  label?: string;
}

const Breadcrumb: React.FC<BreadcrumbProps> = ({
  className,
  hostname: propHostname,
  label,
}) => {
  const router = useRouter();

  // Get current path and hostname
  const currentPath = router.asPath;
  // Remove query parameters to get clean pathname
  const pathname = currentPath.split("?")[0];
  const hostname =
    propHostname ||
    (typeof window !== "undefined" ? window.location.hostname : "");
  const customLabel = label?.trim();

  const generateBreadcrumbs = () => {
    const breadcrumbs = [];

    // Always add Home as first breadcrumb
    breadcrumbs.push({
      label: "Home",
      href: `https://${hostname}/`,
      isActive: false,
    });

    // Check if we're on travel insurance page
    if (pathname.includes("/travel-insurance")) {
      // Check if there's a country in the path
      const countryMatch = pathname.match(/\/travel-insurance\/([^/?]+)/);
      const isOnCountryPage = countryMatch && countryMatch[1];
      const isOnTravelInsurancePage =
        pathname === "/travel-insurance" || pathname === "/travel-insurance/";

      breadcrumbs.push({
        label: "Travel Insurance",
        href: isOnTravelInsurancePage
          ? null
          : `https://${hostname}/travel-insurance/`, // No link if already on travel insurance page
        isActive: isOnTravelInsurancePage, // Active only if on main travel insurance page
      });

      // Add country breadcrumb if on country page
      if (isOnCountryPage) {
        const countrySlug = countryMatch[1];
        // Use provided label when available, else fallback to URL slug.
        const defaultCountryName =
          countrySlug.charAt(0).toUpperCase() + countrySlug.slice(1);

        breadcrumbs.push({
          label: customLabel || defaultCountryName,
          href: null, // No link for current page
          isActive: true,
        });
      }
    }

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();
  const currentPageUrl = hostname
    ? `https://${hostname}${pathname.endsWith("/") ? pathname : `${pathname}/`}`
    : "";
  const breadcrumbSchema =
    breadcrumbs.length > 1 && hostname
      ? {
          "@context": "https://schema.org/",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((breadcrumb, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: breadcrumb.label,
            item: breadcrumb.href || currentPageUrl,
          })),
        }
      : null;

  if (breadcrumbs.length <= 1) {
    return null; // Don't show breadcrumb if only home exists
  }

  return (
    <>
      {breadcrumbSchema && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(breadcrumbSchema),
            }}
          />
        </Head>
      )}
      <BreadcrumbNav aria-label="Breadcrumb" className={className}>
        <BreadcrumbContainer>
          {breadcrumbs.map((breadcrumb, index) => (
            <React.Fragment key={index}>
              <BreadcrumbItem
                isActive={breadcrumb.isActive}
                aria-current={breadcrumb.isActive ? "page" : undefined}
              >
                {breadcrumb.href ? (
                  <BreadcrumbLink
                    href={breadcrumb.href}
                    onClick={(e) => {
                      e.preventDefault();
                      if (breadcrumb.href) {
                        window.location.href = breadcrumb.href;
                      }
                    }}
                  >
                    {breadcrumb.label}
                  </BreadcrumbLink>
                ) : (
                  breadcrumb.label
                )}
              </BreadcrumbItem>
              {index < breadcrumbs.length - 1 && (
                <BreadcrumbSeparator aria-hidden="true">
                  &gt;
                </BreadcrumbSeparator>
              )}
            </React.Fragment>
          ))}
        </BreadcrumbContainer>
      </BreadcrumbNav>
    </>
  );
};

export default Breadcrumb;
